import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Trash2, Building2, Power, PowerOff, Video, Users, Film,
  Calendar, KeyRound, RefreshCw, ShieldCheck, Terminal, CreditCard, Sparkles, Pencil, ShieldAlert,
} from "lucide-react";

export default function SuperAdmin() {
  const [tenants, setTenants] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", admin_username: "", admin_password: "", plan_days: 365 });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    api.get("/tenants").then((r) => setTenants(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [tick]);

  const refresh = () => setTick((t) => t + 1);

  const [showCreds, setShowCreds] = useState(null); // {tenant, admin, operator}

  const submit = async () => {
    try {
      const { data } = await api.post("/tenants", { ...form, plan_days: Number(form.plan_days) });
      toast.success(`Created "${data.tenant.name}"`);
      setOpenNew(false);
      setForm({ name: "", slug: "", admin_username: "", admin_password: "", plan_days: 365 });
      refresh();
      // Show one-time operator credentials modal
      if (data.operator) setShowCreds(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const backfillOperators = async () => {
    try {
      const { data } = await api.post("/tenants/backfill-operators");
      if (data.count === 0) {
        toast.success("All tenants already have operators");
      } else {
        setShowCreds({ backfill: true, created: data.created });
        toast.success(`Created operators for ${data.count} tenant(s)`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (t) => {
    try { await api.patch(`/tenants/${t.id}`, { is_active: !t.is_active }); toast.success(t.is_active ? "Suspended" : "Activated"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (t) => {
    if (!confirm(`Delete tenant "${t.name}" and ALL its data (cameras, footage, users)?`)) return;
    try { await api.delete(`/tenants/${t.id}`); toast.success("Tenant deleted"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const stats = {
    total: tenants.length,
    active: tenants.filter((t) => t.plan_status === "active").length,
    grace: tenants.filter((t) => t.plan_status === "grace").length,
    expired: tenants.filter((t) => t.plan_status === "expired").length,
  };

  return (
    <div className="min-h-screen bg-black text-zinc-100 flex flex-col">
      <AppHeader active="super" />

      <div className="px-4 py-3 border-b border-zinc-900 bg-zinc-950 flex items-center justify-between">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-cyan-400">Super Administration</div>
          <h1 className="font-display text-xl mt-1">Tenant Registry</h1>
        </div>
        <div className="flex items-center gap-3">
          <StatChip label="Total" value={stats.total} color="text-zinc-300" />
          <StatChip label="Active" value={stats.active} color="text-emerald-400" />
          <StatChip label="Grace" value={stats.grace} color="text-amber-400" />
          <StatChip label="Expired" value={stats.expired} color="text-red-400" />
          <Button data-testid="refresh-tenants" variant="ghost" onClick={refresh} className="rounded-none h-8 border border-zinc-800 text-zinc-400"><RefreshCw size={13} /></Button>
          <Button data-testid="backfill-operators-btn" variant="ghost" onClick={backfillOperators} className="rounded-none h-8 border border-amber-500/40 text-amber-400 hover:bg-amber-950/30 text-[10px] uppercase tracking-widest">
            <Terminal size={12} className="mr-1" /> Backfill Ops
          </Button>
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <Button data-testid="add-tenant-btn" className="rounded-none bg-cyan-500 hover:bg-cyan-400 text-black h-8 text-[11px] uppercase tracking-widest">
                <Plus size={14} className="mr-1" /> New Tenant
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none bg-zinc-950 border-zinc-800 text-zinc-100">
              <DialogHeader>
                <DialogTitle className="font-display">Register Tenant Organization</DialogTitle>
                <DialogDescription className="mono text-[10px] text-zinc-500">Provision a new organization, its primary admin, and 1-year plan.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">Organization Name</Label>
                  <Input data-testid="tenant-name" className="mono bg-black border-zinc-800 rounded-none mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">Slug (optional)</Label>
                  <Input data-testid="tenant-slug" className="mono bg-black border-zinc-800 rounded-none mt-1" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto-generated from name" /></div>
                <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">Plan Duration (days)</Label>
                  <Input data-testid="tenant-plan-days" type="number" min="1" className="mono bg-black border-zinc-800 rounded-none mt-1" value={form.plan_days} onChange={(e) => setForm({ ...form, plan_days: e.target.value })} /></div>
                <div className="border-t border-zinc-800 pt-3">
                  <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Primary Admin</div>
                  <div className="space-y-3">
                    <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">Admin Username</Label>
                      <Input data-testid="tenant-admin-username" className="mono bg-black border-zinc-800 rounded-none mt-1" value={form.admin_username} onChange={(e) => setForm({ ...form, admin_username: e.target.value })} /></div>
                    <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">Admin Password</Label>
                      <Input data-testid="tenant-admin-password" type="password" className="mono bg-black border-zinc-800 rounded-none mt-1" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} /></div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="tenant-save" disabled={!form.name || !form.admin_username || !form.admin_password} onClick={submit} className="rounded-none bg-cyan-500 hover:bg-cyan-400 text-black">Create Tenant</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex-1 p-4 overflow-auto">
        {tenants.length === 0 ? (
          <div className="border border-dashed border-zinc-900 p-12 text-center mono text-[11px] text-zinc-600 uppercase tracking-widest">No tenants yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {tenants.map((t) => (
              <TenantCard key={t.id} tenant={t} onChange={refresh} onToggle={() => toggleActive(t)} onDelete={() => del(t)} />
            ))}
          </div>
        )}
      </div>

      <CredentialsModal data={showCreds} onClose={() => setShowCreds(null)} />
    </div>
  );
}

function CredentialsModal({ data, onClose }) {
  if (!data) return null;
  const items = data.backfill
    ? data.created.map((c) => ({ label: c.tenant_name, username: c.username, password: c.password }))
    : [{ label: data.tenant.name, username: data.operator.username, password: data.operator.password }];
  const copyLine = (u, p) => {
    navigator.clipboard.writeText(`${u} / ${p}`).then(() => toast.success("Copied to clipboard"));
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none bg-zinc-950 border-amber-500/50 text-zinc-100 max-w-2xl" data-testid="operator-credentials-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-amber-400">
            <KeyRound size={16} /> Operator Credentials · SAVE NOW
          </DialogTitle>
          <DialogDescription className="mono text-[10px] text-red-400 uppercase tracking-widest">
            ▸ Passwords shown once — closing this dialog will lose them permanently. Regenerate later if needed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[420px] overflow-auto -mx-1 px-1">
          {items.map((it, i) => (
            <div key={i} className="border border-amber-500/40 bg-amber-950/20 p-3">
              <div className="mono text-[10px] uppercase tracking-widest text-amber-400 mb-1">{it.label}</div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="flex-1 mono text-[11px] break-all">
                  <div className="text-zinc-500 text-[9px] uppercase tracking-widest">Username</div>
                  <div className="text-cyan-300">{it.username}</div>
                </div>
                <div className="flex-1 mono text-[11px] break-all">
                  <div className="text-zinc-500 text-[9px] uppercase tracking-widest">Password</div>
                  <div className="text-amber-300">{it.password}</div>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => copyLine(it.username, it.password)}
                  className="rounded-none h-8 border border-amber-500/50 text-amber-400 hover:bg-amber-950/40 text-[10px] uppercase tracking-widest"
                >
                  Copy
                </Button>
              </div>
            </div>
          ))}
        </div>
        <div className="mono text-[10px] text-zinc-500 border-t border-zinc-900 pt-2">
          ▸ Operator login URL: <span className="text-cyan-400">/nvr-console</span>
        </div>
        <DialogFooter>
          <Button
            data-testid="credentials-close"
            onClick={onClose}
            className="rounded-none bg-cyan-500 hover:bg-cyan-400 text-black uppercase tracking-widest text-xs"
          >
            I&apos;ve saved them — Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatChip({ label, value, color }) {
  return (
    <div className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-zinc-800 bg-zinc-950">
      <span className="text-zinc-500 mr-2">{label}</span>
      <span className={color}>{value}</span>
    </div>
  );
}

function TenantCard({ tenant: t, onChange, onToggle, onDelete }) {
  const [renewOpen, setRenewOpen] = useState(false);
  const [opOpen, setOpOpen] = useState(false);

  const status = t.plan_status;
  const days = t.days_remaining;
  const statusColor = status === "expired" ? "border-red-500/60 text-red-400"
    : status === "grace" ? "border-amber-500/60 text-amber-400"
    : "border-emerald-500/60 text-emerald-400";
  const barColor = status === "expired" ? "bg-red-500" : status === "grace" ? "bg-amber-500" : "bg-emerald-500";

  return (
    <div data-testid={`tenant-card-${t.slug}`} className="border border-zinc-900 bg-zinc-950">
      <div className={`h-1 ${t.is_active ? barColor : "bg-zinc-700"}`} />
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 min-w-0">
            <Building2 size={16} className="text-cyan-400 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="font-display text-base truncate">{t.name}</div>
              <div className="mono text-[10px] text-zinc-500 truncate">/{t.slug}</div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${statusColor}`}>{status || "unknown"}</span>
            {!t.is_active && <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-red-500/60 text-red-400">Suspended</span>}
          </div>
        </div>

        {/* Subscription bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between mono text-[10px] text-zinc-500 uppercase tracking-widest">
            <span>Subscription</span>
            <span className={status === "expired" ? "text-red-400" : status === "grace" ? "text-amber-400" : "text-emerald-400"}>
              {days >= 0 ? `${days}d left` : `${Math.abs(days)}d overdue`}
            </span>
          </div>
          <div className="mt-1 h-1.5 bg-zinc-900 relative overflow-hidden">
            <div className={`absolute inset-y-0 left-0 ${barColor}`} style={{ width: `${Math.max(0, Math.min(100, ((days ?? 0) / 365) * 100))}%` }} />
          </div>
          <div className="mono text-[9px] text-zinc-600 mt-1">Ends: {t.plan_end?.split("T")[0] || "—"}</div>
        </div>

        {/* Counts */}
        <div className="grid grid-cols-3 gap-2 mt-4 border-t border-zinc-900 pt-3">
          <StatCol icon={Video} value={t.camera_count} label="Cams" />
          <StatCol icon={Film} value={t.footage_count} label="Footage" />
          <StatCol icon={Users} value={t.user_count} label="Users" />
        </div>

        {/* Operator status */}
        <div className="mt-3 mono text-[10px] flex items-center gap-2">
          <Terminal size={11} className={t.has_operator ? "text-amber-400" : "text-zinc-600"} />
          <span className={t.has_operator ? "text-amber-400" : "text-zinc-500"}>
            Operator: {t.has_operator ? "configured" : "NOT SET"}
          </span>
        </div>

        {/* Plan tier badge */}
        <div className="mt-2 flex items-center justify-between">
          <div className="mono text-[10px] flex items-center gap-2">
            <Sparkles size={11} className={t.plan_tier === "pro" ? "text-cyan-400" : "text-zinc-600"} />
            <span className={t.plan_tier === "pro" ? "text-cyan-400" : "text-zinc-500"}>
              Tier: <span className="uppercase">{t.plan_tier || "free"}</span>
            </span>
            <span className="text-zinc-600">
              · Cams: {t.camera_count}/{t.camera_limit == null ? "∞" : t.camera_limit}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-4 grid grid-cols-2 gap-2">
          <RenewDialog tenant={t} open={renewOpen} setOpen={setRenewOpen} onChange={onChange} />
          <OperatorDialog tenant={t} open={opOpen} setOpen={setOpOpen} onChange={onChange} />
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <EditTenantDialog tenant={t} onChange={onChange} />
          <AdminResetDialog tenant={t} />
        </div>
        <div className="mt-2 flex items-center gap-2">
          <UpgradeButton tenant={t} />
          <Button data-testid={`tenant-toggle-${t.slug}`} variant="ghost" onClick={onToggle} className="rounded-none h-8 text-[11px] uppercase tracking-widest border border-zinc-800 flex-1 hover:bg-zinc-900">
            {t.is_active ? <><PowerOff size={12} className="mr-1" /> Suspend</> : <><Power size={12} className="mr-1" /> Activate</>}
          </Button>
          <Button data-testid={`tenant-delete-${t.slug}`} variant="ghost" onClick={onDelete} className="rounded-none h-8 text-red-400 border border-red-900/40 hover:bg-red-950/40"><Trash2 size={13} /></Button>
        </div>
      </div>
    </div>
  );
}

function EditTenantDialog({ tenant: t, onChange }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: t.name || "", slug: t.slug || "" });
  useEffect(() => {
    if (open) setForm({ name: t.name || "", slug: t.slug || "" });
  }, [open, t]);
  const submit = async () => {
    try {
      const payload = {};
      if (form.name.trim() && form.name.trim() !== t.name) payload.name = form.name.trim();
      if (form.slug.trim() && form.slug.trim() !== t.slug) payload.slug = form.slug.trim();
      if (Object.keys(payload).length === 0) { toast.info("No changes"); setOpen(false); return; }
      await api.patch(`/tenants/${t.id}`, payload);
      toast.success("Tenant updated");
      setOpen(false);
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`tenant-edit-${t.slug}`} variant="ghost" className="rounded-none h-8 text-[11px] uppercase tracking-widest border border-zinc-700 text-zinc-300 hover:bg-zinc-900">
          <Pencil size={12} className="mr-1" /> Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none bg-zinc-950 border-zinc-800 text-zinc-100">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Pencil size={16} className="text-cyan-400" /> Edit Tenant — {t.name}</DialogTitle>
          <DialogDescription className="mono text-[10px] text-zinc-500">Rename the organization or change its slug. Slug must be unique.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Organization name</Label>
            <Input data-testid={`edit-tenant-name-${t.slug}`} className="mono bg-black border-zinc-800 rounded-none mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Slug</Label>
            <Input data-testid={`edit-tenant-slug-${t.slug}`} className="mono bg-black border-zinc-800 rounded-none mt-1" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} />
            <div className="mono text-[9px] text-zinc-600 mt-1">Only lowercase, digits, and dashes.</div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid={`edit-tenant-submit-${t.slug}`} onClick={submit} className="rounded-none bg-cyan-500 hover:bg-cyan-400 text-black uppercase tracking-widest text-xs">Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdminResetDialog({ tenant: t }) {
  const [open, setOpen] = useState(false);
  const [creds, setCreds] = useState(null); // {username, password}
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!confirm(`Reset admin password for "${t.name}"? The old password will stop working immediately.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/tenants/${t.id}/admin/reset-password`);
      setCreds({ username: data.username, password: data.password });
      toast.success("Admin password reset");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setCreds(null); }}>
      <DialogTrigger asChild>
        <Button data-testid={`tenant-admin-reset-${t.slug}`} variant="ghost" className="rounded-none h-8 text-[11px] uppercase tracking-widest border border-red-500/40 text-red-400 hover:bg-red-950/30">
          <ShieldAlert size={12} className="mr-1" /> Reset Admin
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none bg-zinc-950 border-red-500/40 text-zinc-100">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-red-400"><ShieldAlert size={16} /> Reset Tenant Admin Password</DialogTitle>
          <DialogDescription className="mono text-[10px] text-zinc-500">
            Regenerates the primary admin account password for <span className="text-zinc-300">{t.name}</span>. Shown once — copy immediately.
          </DialogDescription>
        </DialogHeader>
        {creds ? (
          <div className="space-y-2">
            <div className="mono text-[10px] uppercase tracking-widest text-red-400">▸ Copy now — shown once</div>
            <div className="border border-red-500/40 bg-red-950/20 p-3 space-y-1 mono text-[11px]">
              <div><span className="text-zinc-500 text-[9px] uppercase tracking-widest">Username: </span><span className="text-cyan-300">{creds.username}</span></div>
              <div><span className="text-zinc-500 text-[9px] uppercase tracking-widest">New password: </span><span className="text-amber-300">{creds.password}</span></div>
            </div>
            <Button
              variant="ghost"
              onClick={() => { navigator.clipboard.writeText(`${creds.username} / ${creds.password}`); toast.success("Copied"); }}
              className="rounded-none w-full border border-amber-500/50 text-amber-400 hover:bg-amber-950/40 text-[10px] uppercase tracking-widest"
              data-testid={`admin-reset-copy-${t.slug}`}
            >
              Copy username + password
            </Button>
          </div>
        ) : (
          <div className="mono text-[10px] text-zinc-500 border border-red-500/30 bg-red-950/10 p-2">
            ▸ This regenerates a new random 14-char password for the tenant&apos;s primary admin. The user must be informed to log in again with the new password. This cannot be undone.
          </div>
        )}
        <DialogFooter>
          {creds ? (
            <Button data-testid={`admin-reset-close-${t.slug}`} onClick={() => { setCreds(null); setOpen(false); }} className="rounded-none bg-cyan-500 hover:bg-cyan-400 text-black uppercase tracking-widest text-xs">
              I&apos;ve saved it — Close
            </Button>
          ) : (
            <Button data-testid={`admin-reset-confirm-${t.slug}`} disabled={busy} onClick={run} className="rounded-none bg-red-500 hover:bg-red-400 text-black uppercase tracking-widest text-xs">
              {busy ? "Resetting..." : "▸ Reset password"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UpgradeButton({ tenant }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState("pro_monthly");
  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: plan,
        tenant_id: tenant.id,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("No checkout URL returned");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid={`tenant-upgrade-${tenant.slug}`}
          variant="ghost"
          className="rounded-none h-8 text-[11px] uppercase tracking-widest border border-cyan-500/40 text-cyan-400 hover:bg-cyan-950/30 flex-1"
        >
          <CreditCard size={12} className="mr-1" /> {tenant.plan_tier === "pro" ? "Extend Pro" : "Upgrade"}
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none bg-zinc-950 border-cyan-500/40 text-zinc-100">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkles size={16} className="text-cyan-400" /> Upgrade — {tenant.name}
          </DialogTitle>
          <DialogDescription className="mono text-[10px] text-zinc-500">
            Pro tier: unlimited cameras, priority support. Pay via Stripe (test mode). You will be redirected to Stripe Checkout.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <button
              data-testid={`upgrade-plan-monthly-${tenant.slug}`}
              onClick={() => setPlan("pro_monthly")}
              className={`text-left border p-3 ${plan === "pro_monthly" ? "border-cyan-500 bg-cyan-950/20" : "border-zinc-800 bg-black"}`}
            >
              <div className="mono text-[10px] uppercase tracking-widest text-cyan-400">Monthly</div>
              <div className="font-display text-xl mt-1">$29<span className="mono text-[10px] text-zinc-500">/mo</span></div>
              <div className="mono text-[9px] text-zinc-600 mt-1">+30 days plan</div>
            </button>
            <button
              data-testid={`upgrade-plan-yearly-${tenant.slug}`}
              onClick={() => setPlan("pro_yearly")}
              className={`text-left border p-3 ${plan === "pro_yearly" ? "border-cyan-500 bg-cyan-950/20" : "border-zinc-800 bg-black"}`}
            >
              <div className="mono text-[10px] uppercase tracking-widest text-cyan-400">Yearly · Save 17%</div>
              <div className="font-display text-xl mt-1">$290<span className="mono text-[10px] text-zinc-500">/yr</span></div>
              <div className="mono text-[9px] text-zinc-600 mt-1">+365 days plan</div>
            </button>
          </div>
          <div className="mono text-[10px] text-zinc-600 border border-zinc-900 p-2">
            ▸ Test card: <span className="text-cyan-400">4242 4242 4242 4242</span> · any future expiry · any CVC
          </div>
        </div>
        <DialogFooter>
          <Button
            data-testid={`upgrade-checkout-${tenant.slug}`}
            disabled={busy}
            onClick={start}
            className="rounded-none bg-cyan-500 hover:bg-cyan-400 text-black uppercase tracking-widest text-xs"
          >
            {busy ? "Redirecting..." : "▸ Pay with Stripe"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatCol({ icon: Icon, value, label }) {
  return (
    <div className="flex flex-col items-center">
      <Icon size={13} className="text-zinc-500" />
      <div className="mono text-lg mt-1">{value ?? 0}</div>
      <div className="mono text-[9px] uppercase tracking-widest text-zinc-600">{label}</div>
    </div>
  );
}

function RenewDialog({ tenant: t, open, setOpen, onChange }) {
  const [days, setDays] = useState(365);
  const submit = async () => {
    try {
      await api.post(`/tenants/${t.id}/renew?days=${Number(days) || 365}`);
      toast.success(`Extended by ${days} days`);
      setOpen(false);
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`tenant-renew-${t.slug}`} variant="ghost" className="rounded-none h-8 text-[11px] uppercase tracking-widest border border-emerald-500/40 text-emerald-400 hover:bg-emerald-950/30">
          <Calendar size={12} className="mr-1" /> Renew
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none bg-zinc-950 border-zinc-800 text-zinc-100">
        <DialogHeader>
          <DialogTitle className="font-display">Renew Subscription — {t.name}</DialogTitle>
          <DialogDescription className="mono text-[10px] text-zinc-500">Extend the plan end date by the specified number of days.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="mono text-[11px] text-zinc-400">Current end: <span className="text-zinc-200">{t.plan_end?.split("T")[0]}</span></div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Extend by (days)</Label>
            <Input data-testid={`renew-days-${t.slug}`} type="number" min="1" className="mono bg-black border-zinc-800 rounded-none mt-1" value={days} onChange={(e) => setDays(e.target.value)} />
          </div>
          <div className="mono text-[10px] text-zinc-600 border border-zinc-900 p-2">If plan is already expired, extension starts from today.</div>
        </div>
        <DialogFooter>
          <Button data-testid={`renew-submit-${t.slug}`} onClick={submit} className="rounded-none bg-emerald-500 hover:bg-emerald-400 text-black">Extend Plan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OperatorDialog({ tenant: t, open, setOpen, onChange }) {
  const [form, setForm] = useState({ username: "", password: "" });
  const [regen, setRegen] = useState(null); // {username, password} to show ONCE
  useEffect(() => {
    if (open) {
      setRegen(null);
      api.get(`/tenants/${t.id}/operator`).then((r) => {
        if (r.data?.username) setForm((f) => ({ ...f, username: r.data.username, password: "" }));
        else setForm({ username: "", password: "" });
      }).catch(() => {});
    }
  }, [open, t.id]);

  const generatePassword = () => {
    // 14-char strong, matches backend charset (no confusable chars)
    const A = "ABCDEFGHJKLMNPQRSTUVWXYZ";
    const l = "abcdefghijkmnopqrstuvwxyz";
    const d = "23456789";
    const s = "!@#";
    const pool = A + l + d + s;
    const rand = (arr) => arr[Math.floor(Math.random() * arr.length)];
    // ensure diversity
    let pw = rand(A) + rand(l) + rand(d) + rand(s);
    for (let i = 0; i < 10; i++) pw += rand(pool);
    pw = pw.split("").sort(() => Math.random() - 0.5).join("");
    setForm((f) => ({ ...f, password: pw }));
  };

  const regenerate = async () => {
    if (!confirm("Regenerate password for this tenant's operator? Old password will stop working immediately.")) return;
    try {
      const { data } = await api.post(`/tenants/${t.id}/operator/regenerate`);
      setRegen({ username: data.username, password: data.password });
      toast.success("Password regenerated");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submit = async () => {
    try {
      await api.put(`/tenants/${t.id}/operator`, form);
      toast.success(t.has_operator ? "Operator credentials updated" : "Operator created");
      setOpen(false);
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`tenant-operator-${t.slug}`} variant="ghost" className="rounded-none h-8 text-[11px] uppercase tracking-widest border border-amber-500/40 text-amber-400 hover:bg-amber-950/30">
          <KeyRound size={12} className="mr-1" /> Operator
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none bg-zinc-950 border-amber-500/40 text-zinc-100">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Terminal size={16} className="text-amber-400" /> {t.has_operator ? "Update" : "Set"} Operator Credentials</DialogTitle>
          <DialogDescription className="mono text-[10px] text-zinc-500">
            Operator accesses the hidden video-management console at <span className="text-amber-400">/nvr-console</span> with these credentials. One operator per tenant.
          </DialogDescription>
        </DialogHeader>

        {regen ? (
          <div className="space-y-2">
            <div className="mono text-[10px] uppercase tracking-widest text-red-400">▸ Copy now — shown once</div>
            <div className="border border-amber-500/40 bg-amber-950/20 p-3 space-y-1 mono text-[11px]">
              <div><span className="text-zinc-500 text-[9px] uppercase tracking-widest">Username: </span><span className="text-cyan-300">{regen.username}</span></div>
              <div><span className="text-zinc-500 text-[9px] uppercase tracking-widest">Password: </span><span className="text-amber-300">{regen.password}</span></div>
            </div>
            <Button
              variant="ghost"
              onClick={() => { navigator.clipboard.writeText(`${regen.username} / ${regen.password}`); toast.success("Copied"); }}
              className="rounded-none w-full border border-amber-500/50 text-amber-400 hover:bg-amber-950/40 text-[10px] uppercase tracking-widest"
            >
              Copy username + password
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">Operator ID (username)</Label>
              <Input data-testid={`op-username-${t.slug}`} className="mono bg-black border-zinc-800 rounded-none mt-1" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} /></div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Passphrase</Label>
                <button
                  type="button"
                  onClick={generatePassword}
                  className="mono text-[9px] uppercase tracking-widest text-cyan-400 hover:text-cyan-300 border border-cyan-500/40 px-1.5 py-0.5"
                  data-testid={`op-generate-password-${t.slug}`}
                >
                  ▸ Generate strong
                </button>
              </div>
              <Input data-testid={`op-password-${t.slug}`} type="text" placeholder="Enter a new passphrase or generate one" className="mono bg-black border-zinc-800 rounded-none mt-1" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>
            {t.has_operator && (
              <button
                type="button"
                onClick={regenerate}
                className="mono text-[10px] uppercase tracking-widest text-red-400 hover:text-red-300 border border-red-500/40 px-2 py-1 w-full"
                data-testid={`op-regenerate-${t.slug}`}
              >
                🔑 Regenerate password (invalidates current)
              </button>
            )}
            <div className="mono text-[10px] text-zinc-600 border border-amber-500/30 bg-amber-950/20 p-2 flex items-start gap-2">
              <ShieldCheck size={12} className="text-amber-400 mt-0.5" />
              <span>Give these credentials only to trusted operators. They can upload/manage video content but cannot see users or subscription.</span>
            </div>
          </div>
        )}
        <DialogFooter>
          {regen ? (
            <Button data-testid={`op-close-regen-${t.slug}`} onClick={() => { setRegen(null); setOpen(false); }} className="rounded-none bg-cyan-500 hover:bg-cyan-400 text-black uppercase tracking-widest text-xs">
              I&apos;ve saved it — Close
            </Button>
          ) : (
            <Button data-testid={`op-save-${t.slug}`} disabled={!form.username || !form.password} onClick={submit} className="rounded-none bg-amber-500 hover:bg-amber-400 text-black">
              {t.has_operator ? "Update Operator" : "Create Operator"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
